import { useEffect } from "react";
import TextInput from "../../../../../../../components/core/TextInput/TextInput";
import {
  ADD_LINKEDIN_LABEL,
  CANCEL_LABEL,
  CONFIRM_LABEL,
  EDIT_LINKEDIN_LABEL,
  IS_NOT_VALID_URL_LABEL,
  LINKEDIN_PROFILE_LABEL,
} from "../../../../../../../constants/Dictionary";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import * as yup from "yup";
import { ReactComponent as CloseIcon } from "../../../../../../../icons/close.svg";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { StyledLinkedinModal } from "./LinkedinModal.styled";
import { CollaboratorDetails } from "../../../../../../../types/api/collaborators.type";
import {
  useLinkedinUrlAdd,
  useLinkedinUrlUpdate,
} from "../../../../../../../hooks/professionalPortrait.hook";

type LinkedinModalProps = {
  isOpen: boolean;
  linkedInUrl?: string;
  professionalDataId?: number;
  onSave: () => void;
  onClose: () => void;
  collaborator: CollaboratorDetails;
};

export type FormData = {
  url?: string;
};

export const LinkedinModal = ({
  isOpen,
  linkedInUrl,
  professionalDataId,
  onSave,
  onClose,
  collaborator,
}: LinkedinModalProps) => {
  const profileId = collaborator.id;

  const {
    mutateAsync: updateLinkedinUrl,
    isPending: pendingUpdateLinkedinUrl,
  } = useLinkedinUrlUpdate(profileId);
  const { mutateAsync: addLinkedinUrl, isPending: pendingAddLinkedinUrl } =
    useLinkedinUrlAdd(profileId);

  const hasExistingData = !!professionalDataId;

  const schema = yup
    .object({
      url: yup
        .string()
        .transform((value) => {
          if (!value) return undefined;
          if (!/^https?:\/\//i.test(value)) value = `https://${value}`;
          return value;
        })
        .url(`${LINKEDIN_PROFILE_LABEL} ${IS_NOT_VALID_URL_LABEL}`)
        .matches(/linkedin\.com/, "Il valore deve essere una URL di LinkedIn")
        .optional(),
    })
    .required();

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (isOpen) {
      reset({
        url: linkedInUrl || "",
      });
    } else {
      reset({
        url: "",
      });
    }
  }, [isOpen, linkedInUrl, reset]);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (hasExistingData && professionalDataId) {
      await updateLinkedinUrl({ data, professionalDataId });
    } else {
      await addLinkedinUrl(data);
    }
    onSave();
  };

  return (
    <Modal
      fullSize={false}
      label={linkedInUrl ? EDIT_LINKEDIN_LABEL : ADD_LINKEDIN_LABEL}
      isOpen={isOpen}
      leftButton={
        <Button
          label={CANCEL_LABEL}
          variant={ButtonVariants.SECONDARY}
          icon={<CloseIcon />}
          onClick={onClose}
          disabled={pendingUpdateLinkedinUrl || pendingAddLinkedinUrl}
        />
      }
      rightButton={
        <Button
          label={CONFIRM_LABEL}
          variant={ButtonVariants.PRIMARY}
          icon={<CheckIcon />}
          disabled={
            !isDirty || pendingUpdateLinkedinUrl || pendingAddLinkedinUrl
          }
          type="submit"
          form="linkedinForm"
        />
      }
    >
      <StyledLinkedinModal>
        <Loader loading={pendingAddLinkedinUrl || pendingUpdateLinkedinUrl}>
          <form id="linkedinForm" onSubmit={handleSubmit(onSubmit)}>
            <div className="input-row">
              <FormField error={errors.url?.message}>
                <Controller
                  name="url"
                  control={control}
                  render={({ field }) => (
                    <TextInput label={LINKEDIN_PROFILE_LABEL} {...field} />
                  )}
                />
              </FormField>
            </div>
          </form>
        </Loader>
      </StyledLinkedinModal>
    </Modal>
  );
};
